"use client";

import { useMemo } from "react";
import { SUPPORTED_EXCHANGES } from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import { WALLET_STATUS } from "@/constants/wallet";
import { useAuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";
import { useApi } from "@/hooks/core/useApi";
import { useAuthenticatedWalletConnection } from "@/hooks/core/useAuthenticatedWalletConnection";
import { useSignTypedDataWithConnection } from "@/hooks/core/useSignTypedDataWithConnection";
import { isWalletActionError, useWalletActionGuard } from "@/hooks/core/useWalletActionGuard";
import { useHubUser } from "@/hooks/user/useHubUser";
import {
  fetchParadexSystemConfig,
  buildStarkKeyTypedData,
  deriveParadexKeys,
} from "@/utils/airdrop-farming/paradex-account";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useAccount } from "wagmi";

/**
 * Session status for Paradex connection flow.
 * Uses a subset of HyperliquidSessionStatus to stay compatible with ConnectAccountStep.
 */
export type ParadexSessionStatus =
  | "checking-wallet"
  | "needs-wallet"
  | "needs-approval"
  | "checking-activation"
  | "ready";

/**
 * Hook for managing Paradex connection flow.
 *
 * Flow:
 * 1. User connects their EVM wallet (wagmi)
 * 2. User signs a single EIP-712 "STARK Key" message
 * 3. The StarkNet private key, public key, and account address are derived automatically
 * 4. The derived credentials are sent to the backend which calls Paradex onboarding API
 * 5. The account is stored in SSM and ready for airdrop farming bots
 *
 * Security: The derived StarkNet key only authorises trading on Paradex — it cannot
 * withdraw funds from the EVM wallet.
 */
export function useParadexConnection() {
  const { auth, hubUser, walletStatus } = useHubUser();
  const apiHelper = useApi();
  const { address: wagmiAddress } = useAccount();
  const { authenticated } = useAuthenticationProvider();
  const queryClient = useQueryClient();
  const { authenticatedWalletAddress } = useAuthenticatedWalletConnection();
  const { prepareMainEvmAction } = useWalletActionGuard();
  const { signTypedDataAsync } = useSignTypedDataWithConnection();
  const requiredWalletAddress = authenticatedWalletAddress ?? wagmiAddress ?? hubUser?.defiAddress ?? undefined;
  const connectedAddress = walletStatus === WALLET_STATUS.CONNECTED ? requiredWalletAddress : undefined;
  const isWalletConnected = walletStatus === WALLET_STATUS.CONNECTED && Boolean(requiredWalletAddress);

  const paradexAccounts = useMemo(
    () => hubUser?.exchangeAccounts?.filter((a) => a.exchangeId === SUPPORTED_EXCHANGES.PARADEX) ?? [],
    [hubUser?.exchangeAccounts],
  );

  const hasParadexAccount = paradexAccounts.length > 0;

  const {
    mutateAsync: connectParadex,
    isPending: isConnecting,
    error: connectionError,
    reset: resetConnectionError,
  } = useMutation({
    mutationFn: async () => {
      const { address: walletAddress } = await prepareMainEvmAction();

      // 1. Fetch Paradex system config (class hashes + chain ID)
      const systemConfig = await fetchParadexSystemConfig();

      // 2. Sign EIP-712 "STARK Key" message to derive keys
      const typedData = buildStarkKeyTypedData(systemConfig.l1_chain_id);
      const signature = await signTypedDataAsync(typedData);

      // 3. Derive StarkNet private key, public key and account address from signature
      const { privateKey, accountAddress } = deriveParadexKeys(
        signature,
        systemConfig.paraclear_account_hash,
        systemConfig.paraclear_account_proxy_hash,
      );

      // 4. Send derived keys to backend (backend will call Paradex onboarding API)
      try {
        await apiHelper.postAuth(auth, API_ENDPOINT.EXCHANGE_ACCOUNT_ADD_PARADEX, {
          bodyParams: {
            starknetPrivateKey: privateKey,
            walletAddress,
            accountAddress,
          },
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const normalizedMessage = errorMessage.toLowerCase();
        const isParadexOnboardingIssue =
          normalizedMessage.includes("not_onboarded") ||
          normalizedMessage.includes("user has never called the /onboarding endpoint") ||
          normalizedMessage.includes("paradex onboarding failed");
        const hasMinBalanceIssue = normalizedMessage.includes("insufficient_min_chain_balance");

        if (hasMinBalanceIssue) {
          throw new Error(
            "Paradex requires this wallet to hold at least 0.001 ETH or 5 USDC on Ethereum, Arbitrum, or Base before the account can be created.",
          );
        }

        if (isParadexOnboardingIssue) {
          throw new Error(
            "Paradex account setup was not completed yet. Please sign again to finish the one-time Paradex onboarding step.",
          );
        }

        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      enqueueSnackbar("Paradex account connected successfully", { variant: "success" });
    },
    onError: (error) => {
      if (isWalletActionError(error)) return;
      const msg = error instanceof Error ? error.message : "Failed to connect Paradex";
      enqueueSnackbar(msg, { variant: "error" });
    },
  });

  const sessionStatus: ParadexSessionStatus =
    walletStatus === WALLET_STATUS.INITIALIZING || walletStatus === WALLET_STATUS.CONNECTING
      ? "checking-wallet"
      : !authenticated || !requiredWalletAddress
        ? "needs-wallet"
        : isConnecting
          ? "checking-activation"
          : hasParadexAccount
            ? "ready"
            : "needs-approval";

  return {
    // Wallet state
    connectedAddress,
    isWalletConnected,

    // Account state
    paradexAccounts,
    hasParadexAccount,

    // Actions
    connectParadex,
    isConnecting,
    connectionError,
    resetConnectionError,

    // Session status (compatible with ConnectAccountStep)
    sessionStatus,

    // ── Shape compatible with useHyperliquidConnection for useLaunchBotForm ──
    agentData: {
      accountId: paradexAccounts[0]?.id ?? 0,
      agentAddress: paradexAccounts[0]?.publicApiKey ?? "",
      agentName: "",
    },
    hasAgentData: hasParadexAccount,
    isLoadingAgent: false,
    isCheckingEligibility: false,
    isNotEligibleError: false,
    eligibilityData: { eligible: true } as const,
    isEligible: true,
    // Map Paradex connect → HL approve shape
    approveAgent: async () => connectParadex(),
    isApproving: isConnecting,
    approvalError: connectionError,
    resetApprovalError: resetConnectionError,
    // Not applicable for Paradex
    updateHyperliquidAgentStatus: async () => {},
    isUpdatingAgentStatus: false,
    isLoading: isConnecting,
    walletStatus,

    // Authenticated check
    authenticated,
  };
}
